import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, BarChart3 } from "lucide-react";
import { toast } from "sonner";

function GapList({ title, rows, testId, label = "question" }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      <p className="px-3 py-2 text-xs font-semibold uppercase text-slate-500 border-b border-slate-100">{title}</p>
      {rows?.length ? <ul className="divide-y divide-slate-100">{rows.map((r, i) => <li key={i} className="px-3 py-1.5 text-sm flex justify-between gap-2"><span className="text-slate-700 truncate">{r[label]}</span><span className="text-slate-400">{r.count}</span></li>)}</ul>
        : <p className="px-3 py-2 text-xs text-slate-400">None yet.</p>}
    </div>
  );
}

export function SystemDocsPanel() {
  const [st, setSt] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/help/admin/system-docs/status").then((r) => setSt(r.data)).catch(() => {});
    api.get("/help/admin/knowledge-gaps").then((r) => setGaps(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const sync = async () => {
    if (!window.confirm("Update the Top-Recruit Systems documentation? Articles your team edited are not overwritten; they're flagged Needs Review instead.")) return;
    setBusy(true);
    try { const { data } = await api.post("/help/admin/system-docs/sync"); toast.success(`Updated: ${data.created} new, ${data.updated} refreshed, ${data.flagged_review} flagged for review`); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="system-docs-panel">
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-slate-900">Top-Recruit Systems documentation</p>
          <p className="text-sm text-slate-500" data-testid="system-docs-status">{st ? `${st.published} published · ${st.needs_review} need review · ${st.in_set} in the current set` : "Loading…"}</p>
        </div>
        {st?.can_sync && <Button onClick={sync} disabled={busy} className="bg-brand hover:bg-brand-strong text-white" data-testid="system-docs-sync"><RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />Update Top-Recruit Systems Knowledge</Button>}
      </div>
      <div>
        <p className="font-semibold text-slate-900 flex items-center gap-2 mb-2"><BarChart3 className="w-4 h-4 text-slate-400" />Knowledge gaps {gaps && <span className="text-xs font-normal text-slate-400">· {gaps.total} questions asked</span>}</p>
        <div className="grid md:grid-cols-2 gap-3">
          <GapList title="Unanswered questions" rows={gaps?.unanswered} testId="gaps-unanswered" />
          <GapList title="Low-confidence answers" rows={gaps?.low_confidence} testId="gaps-low" />
          <GapList title="Led to a support ticket" rows={gaps?.led_to_ticket} testId="gaps-ticket" />
          <GapList title="Articles marked unhelpful" rows={gaps?.unhelpful_articles} testId="gaps-unhelpful" label="ref" />
        </div>
      </div>
    </div>
  );
}
